import { FC, useEffect, useState } from "react";
import {
  Box,
  DialogActionTrigger,
  DialogCloseTrigger,
  DialogFooter,
  DialogRoot,
  DropsButton,
  DropsDialogContent,
  HStack,
  Text,
  Textarea,
  tokens,
} from "shared/components/Spor";
import { InfrastructureIncidentIcon18 } from "shared/icons/InfrastructureIncidentIcon18";
import { useTheme } from "styled-components";

export type MuteUnmuteDialogMode = "mute" | "unmute";

type MuteEventDialogProps = {
  open: boolean;
  setopen: (open: boolean) => void;
  eventTitle: string;
  mode: MuteUnmuteDialogMode;
  onMuted?: (reason: string) => void;
  onUnmuted?: (reason: string) => void;
};

const MuteEventDialog: FC<MuteEventDialogProps> = ({
  open,
  setopen,
  eventTitle,
  mode,
  onMuted,
  onUnmuted,
}) => {
  const theme = useTheme();
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (open) setReason("");
  }, [open]);

  const isUnmute = mode === "unmute";
  const title = isUnmute ? "Fremhev hendelse" : "Demp hendelse";
  const textareaLabel = isUnmute
    ? "Beskriv hvorfor hendelsen trenger tiltak likevel..."
    : "Beskriv hvorfor hendelsen ikke trenger tiltak...";
  const confirmLabel = isUnmute ? "Fremhev hendelse" : "Demp hendelse";
  const warningText = isUnmute
    ? "OBS! Hendelsen vil fremheves på nytt i oversikten. Bruk dette når noe krever oppfølging likevel."
    : "OBS! Når du demper hendelsen vil den ikke lenger være markert som viktig. Den blir fortsatt liggende i kolonnen for hendelser uten tiltak.";

  const handleConfirm = () => {
    if (isUnmute) {
      onUnmuted?.(reason);
    } else {
      onMuted?.(reason);
    }
    setopen(false);
  };

  return (
    <DialogRoot open={open} size="sm" onOpenChange={() => setopen(false)}>
      <DropsDialogContent
        overflowY="scroll"
        backgroundColor={theme.colorBackgroundMain}
        width="448px"
      >
        <Box p={tokens.default.size.spacing[4]}>
          <Text variant="md" fontWeight="bold">
            {title}
          </Text>
          <DialogCloseTrigger />

          <HStack
            gap={tokens.default.size.spacing[1]}
            mt={tokens.default.size.spacing[1]}
          >
            <InfrastructureIncidentIcon18 />
            <Text variant="sm" fontWeight="bold">
              {eventTitle}
            </Text>
          </HStack>
        </Box>

        <Box
          border={`1px solid ${theme.colorOutlineSecondary}`}
          borderRadius={tokens.default.size.spacing[2]}
          m={tokens.default.size.spacing[1]}
          p={tokens.default.size.spacing[3]}
          backgroundColor={theme.colorBackgroundSecondary}
        >
          <Textarea
            label={textareaLabel}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            autoFocus
            css={`
              width: 100%;
              transition: all 250ms ease;
              resize: none;
              min-height: 7em;
            `}
          />
          <Text mt={tokens.default.size.spacing[2]} variant="xs">
            {warningText}
          </Text>
        </Box>
        <DialogFooter>
          <DialogActionTrigger asChild>
            <DropsButton
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setopen(false);
              }}
              ml="0px"
              mr="auto"
            >
              Avbryt
            </DropsButton>
          </DialogActionTrigger>
          <DropsButton
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              handleConfirm();
            }}
            ml="auto"
            mr="0px"
          >
            {confirmLabel}
          </DropsButton>
        </DialogFooter>
      </DropsDialogContent>
    </DialogRoot>
  );
};

export default MuteEventDialog;
